import { create } from 'zustand';
import { adminProductManagementApi, adminCategoryManagementApi } from '@api/api-config';
import { AdminProductListResponse, AdminCategoryListResponse } from '@api/generated/portal-be-admin';

interface ProductSearchState {
  // Seznam produktů
  products: AdminProductListResponse[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;

  // Loading a error stavy
  isLoading: boolean;
  error: string | null;

  // Cache kategorií pro mapování ecid -> název
  categoryCache: Map<string, string>;

  // Filtry
  filters: {
    categoryEcid?: string;
    name?: string;
  };
}

interface ProductSearchActions {
  // Načtení seznamu produktů
  fetchProducts: (params?: { categoryEcid?: string; name?: string; page?: number; size?: number }) => Promise<void>;

  // Načtení kategorií pro mapování
  loadCategoryCache: () => Promise<void>;

  // Nastavení filtrů
  setFilters: (filters: Partial<ProductSearchState['filters']>) => void;
  clearFilters: () => void;

  // Utility
  clearError: () => void;
}

export const useProductSearchStore = create<ProductSearchState & ProductSearchActions>((set, get) => ({
  // Initial state
  products: [],
  totalElements: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
  categoryCache: new Map(),
  filters: {},

  // Actions
  loadCategoryCache: async () => {
    try {
      const response = await adminCategoryManagementApi.getCategories({ size: 999999 });
      const categoryMap = new Map<string, string>();
      response.data.items.forEach((cat: AdminCategoryListResponse) => {
        if (cat.ecid && cat.name) {
          categoryMap.set(cat.ecid, cat.name);
        }
      });
      set({ categoryCache: categoryMap });
    } catch (err) {
      console.error('Nepodařilo se načíst kategorie pro cache:', err);
    }
  },
  fetchProducts: async (params = {}) => {
    set({ isLoading: true, error: null });

    try {
      const response = await adminProductManagementApi.getProducts({
        page: params.page ?? get().currentPage,
        size: params.size ?? get().pageSize,
        query: params.name,
        categoryEcid: params.categoryEcid,
      });

      set({
        products: response.data.items,
        totalElements: response.data.total,
        totalPages: response.data.totalPages,
        currentPage: response.data.page || 1,
        pageSize: response.data.size || 20,
        filters: {
          categoryEcid: params.categoryEcid,
          name: params.name,
        },
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst produkty';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        categoryEcid: undefined,
        name: undefined,
      },
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));
